import _ from 'lodash';
import {
  colors,
  createTheme as createMuiTheme,
  responsiveFontSizes,
} from '@mui/material';
import { THEMES } from 'src/constants';
import { softShadows, strongShadows } from './shadows';
import typography from './typography';
import palette from './palette';

const isColor = (strColor) => {
  const s = new Option().style;
  s.color = strColor;
  return s.color !== '';
};

const relLuminance = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return null;
  const n = parseInt(m[1], 16);
  const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
};

// WCAG 1.4.3: pick white or dark ink so button label text stays >= 4.5:1
// against its background (e.g. brandOrange keeps its hue but gets dark text).
export const readableInk = (bg) => {
  const L = relLuminance(bg);
  if (L == null) return '#FFFFFF';
  const whiteContrast = (1 + 0.05) / (L + 0.05);
  return whiteContrast >= 4.5 ? '#FFFFFF' : '#1A1A1A';
};

// Single source of truth for the orange surface + the only ink that passes on
// it. White on brandOrange is 2.8:1, so anything sitting on the orange must use
// BRAND_ORANGE_INK (6.3:1). Exported for the few styled objects that are built
// outside the theme; everything with a `theme` should read
// `theme.palette.brandOrange` / `theme.palette.brandOrangeInk`.
export const BRAND_ORANGE = '#EF7B43';
export const BRAND_ORANGE_INK = readableInk(BRAND_ORANGE);

// The other direction: orange used *as text* on a light background. The brand
// hue is only 2.8:1 on white, so text takes this darkened shade (4.65:1).
export const BRAND_ORANGE_TEXT = '#C2521A';

const baseOptions = {
  typography,
  overrides: {
    MuiLinearProgress: {
      root: {
        borderRadius: 3,
        overflow: 'hidden',
      },
    },
    MuiListItemIcon: {
      root: {
        minWidth: 32,
      },
    },
    MuiChip: {
      root: {
        backgroundColor: 'rgba(0,0,0,0.075)',
      },
    },
    MuiTableCell: {
      root: {
        padding: 10,
      },
    },
    MuiFormHelperText: {
      root: {
        backgroundColor: '#FFF4F2',
        color: '#d32f2f',
        marginTop: '1px',
      },
      contained: {
        marginLeft: 'unset',
        marginRight: 'unset',
        padding: '0px 14px',
      },
    },
  },

  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          t0: 'p',
          t1: 'p',
          t2: 'p',
          t3: 'p',
          t3_light: 'p',
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          '& .MuiOutlinedInput-notchedOutline': {
            border: '1px solid #6B6F77',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: '2px solid #2A77BE',
          },
        },
      },
    },

    MuiAutocomplete: {
      styleOverrides: {
        option: {
          '&[aria-selected="true"]': {
            backgroundColor: '#E9F2FA',
          },
          '&[aria-selected="true"].Mui-focused': {
            backgroundColor: '#E9F2FA',
          },
          '&.Mui-focused': {
            backgroundColor: '#F5F5F5',
          },
        },
        listbox: {
          padding: 0,
        },
      },
    },

    MuiButtonBase: {
      defaultProps: {
        tabIndex: null, // Remove tabindex from all ButtonBase components
      },
    },

    MuiButton: {
      styleOverrides: {
        contained: ({ theme, maincolor }) => {
          const mainColorResolved =
            theme.palette[maincolor] ||
            (isColor(maincolor) && maincolor) ||
            theme.palette.brandBlue;
          return {
            fontSize: 16,
            fontWeight: 700,
            borderRadius: 6,
            backgroundColor: mainColorResolved,
            color: readableInk(mainColorResolved),

            '&:disabled': {
              backgroundColor: '#97C2E9',
              color: mainColorResolved,
            },
            '&:hover': {
              backgroundColor: mainColorResolved,
            },
          };
        },
        outlined: ({ theme }) => ({
          fontSize: 16,
          fontWeight: 700,
          borderRadius: 6,
          border: `3px solid ${theme.palette.brandBlue}`,
          color: theme.palette.brandBlue,
          '&:hover': {
            border: `3px solid ${theme.palette.brandBlue}`,
          },
        }),
      },
    },

    MuiInputBase: {
      styleOverrides: {
        input: {
          '&::placeholder': {
            opacity: 1,
            color: '#6B6F77',
          },
        },
      },
    },
  },
};

const themesOptions = [
  {
    name: THEMES.LIGHT,
    palette: {
      mode: 'light',
      // brandBlue/brandRed darkened for WCAG 1.4.3 AA: white text now >= 4.5:1
      // (was #2F84D3 3.92:1 / #E5504F 3.76:1). brandOrange keeps its hue and
      // carries dark ink instead -- see BRAND_ORANGE_INK.
      brandBlue: '#2A77BE',
      brandBlueDisabled: '#97C2E9',
      brandDarkBlue: '#245284',
      brandOrange: BRAND_ORANGE,
      brandOrangeInk: BRAND_ORANGE_INK,
      brandOrangeText: BRAND_ORANGE_TEXT,
      brandRed: '#CE4847',
      panelBlue: '#FAFBFF',
      lightBlue: '#E9F2FA',
      text: {
        primary: '#353C45',
        alternative: '#FFFFFF',
        disabled: '#6B6F77',
      },
      action: {
        active: colors.blueGrey[600],
      },
      background: {
        default: colors.common.white,
      },
    },
    shadows: softShadows,
  },
];

export const createTheme = (config = {}) => {
  const themeOptions = themesOptions.find(
    (theme) => theme.name === config.theme,
  );

  if (!themeOptions) {
    console.warn(new Error(`The theme ${config.theme} is not valid`));
    [themeOptions] = themesOptions;
  }

  const theme = responsiveFontSizes(
    createMuiTheme(_.merge({}, baseOptions, themeOptions)),
  );

  return theme;
};
